import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { api, ensurePatientToken } from '@/lib/api'
import { isNative } from '@/lib/platform'
import { clearPatientToken, clearTherapistToken } from '@/lib/auth-token'
import { setTelemetryUser } from '@/lib/telemetry'
import { forgetForErrorReporting, identifyForErrorReporting } from '@/lib/telemetry/errorReporting'
import {
  trackOnboardingBootstrapFailed,
  trackOnboardingBootstrapSucceeded,
} from '@/lib/telemetry-events'

/**
 * Tracks the *current authenticated identity* for the session.
 *
 * `kind` discriminates the two flows:
 *   - 'patient'   → eternal Sanctum token, native app
 *   - 'therapist' → upstream-issued JWT, web app
 *   - null        → unauthenticated (web app, before the JWT lands)
 *
 * The store is intentionally small: the source of truth for the *token*
 * lives in `lib/auth-token.ts` (Capacitor Preferences / localStorage), and
 * the source of truth for the *server-side identity* is the API response
 * from `/api/{patient|therapist}/me`. The store just caches the latter.
 */
export const useAuthStore = defineStore('auth', () => {
  const kind = ref<'patient' | 'therapist' | null>(null)
  const id = ref<string | null>(null)
  const externalId = ref<string | null>(null)

  /**
   * Display name of the practice this patient is linked to, or null. Hydrated
   * from `/api/patient/me`.
   *
   * **Display only.** `practices.name` is nullable — Therapist\SessionController
   * creates a practice row straight from a JWT with no name, and it only gains
   * one if the portal later mints a pairing code for it — so a null name says
   * nothing about whether a link exists.
   */
  const practiceName = ref<string | null>(null)

  /**
   * Whether this patient is linked to a practice, as reported by the server.
   *
   * Previously derived from `practiceName !== null`, which read a perfectly
   * good link to a nameless practice as no link at all: the pairing dialog
   * showed its success state, `hasTherapist` stayed false, and every later
   * launch re-read the same null name and agreed. The server now answers the
   * question directly (`patients.practice_id !== null`).
   */
  const linked = ref(false)

  /** Derived: true iff the patient is linked to a practice. */
  const hasTherapist = computed(() => linked.value)

  /** Native flow: ensure a token exists, then load /me. */
  async function bootstrapPatient(): Promise<void> {
    try {
      const token = await ensurePatientToken()
      // Identify the patient for TelemetryDeck. App.vue already sets the
      // user from the token on returning launches; on a true first launch
      // this is the earliest point we have it. Both paths feed the SDK the
      // same token so signals stitch under one hash for the device.
      setTelemetryUser(token)
      const { data } = await api.get<{
        id: string
        linked?: boolean
        practice_name: string | null
      }>('/patient/me')
      kind.value = 'patient'
      id.value = data.id
      externalId.value = null
      practiceName.value = data.practice_name ?? null
      // `linked` is authoritative; the name fallback only covers a server that
      // predates the field, where a non-null name still implies a link.
      linked.value = data.linked ?? data.practice_name !== null
      // Opaque id only - never the patient row, never the token. Lets an issue
      // be traced to one device without the tracker learning who that is.
      identifyForErrorReporting(data.id, 'patient')
      trackOnboardingBootstrapSucceeded()
    } catch (e) {
      trackOnboardingBootstrapFailed(extractErrorCode(e))
      throw e
    }
  }

  function extractErrorCode(e: unknown): string {
    if (e && typeof e === 'object' && 'response' in e) {
      const status = (e as { response?: { status?: unknown } }).response?.status
      if (typeof status === 'number') return `http_${status}`
    }
    return 'network'
  }

  /** Web flow: assume a JWT is already attached by the request interceptor. */
  async function loadTherapist(): Promise<void> {
    const { data } = await api.get<{ id: string; external_id: string }>('/therapist/me')
    kind.value = 'therapist'
    id.value = String(data.id)
    externalId.value = data.external_id
    identifyForErrorReporting(String(data.id), 'therapist')
  }

  async function logout(): Promise<void> {
    if (isNative) {
      await clearPatientToken()
    } else {
      clearTherapistToken()
    }
    kind.value = null
    id.value = null
    externalId.value = null
    practiceName.value = null
    linked.value = false

    // Sentry's scope outlives the session; without this, an error after logout
    // is still attributed to whoever just signed out.
    forgetForErrorReporting()
  }

  return {
    kind,
    id,
    externalId,
    practiceName,
    linked,
    hasTherapist,
    bootstrapPatient,
    loadTherapist,
    logout,
  }
})
